import { useApiQuery } from '@/api/main/query'
import { PaymentRequisitIcon } from '@/entities/paymentRequisit'
import { TopupModel } from '@/entities/topup'
import { topupsAPI, TopupStatus, TopupStatusIcons, TopupStatusTitles } from '@/entities/topups'
import { useGetDeviceData } from '@/features/devices/hooks/useGetDeviceData'
import { DeviceStatusIcons } from '@/features/devices/types/constants'
import { formatRequisitToType } from '@/features/requisites'

import { BaseDialog, BaseDialogSkeleton, BaseInput } from '@/shared/components'
import { ItemLayout } from '@/shared/components/ItemLayout/ItemLayout'
import { ItemLayoutSkeleton } from '@/shared/components/Skeletones/ItemLayoutSkeleton'
import { DealIcon } from '@/shared/components/icons/DealIcon'
import { DeviceIcon } from '@/shared/components/icons/DeviceIcon'
import { OrderIcon } from '@/shared/components/icons/OrderIcon'
import { useToast } from '@/shared/hooks'
import { formatDate } from '@/shared/lib/date'
import { getTimeAgoFormatted } from '@/shared/utils/getTimeAgoFormatted'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useGetDisputData } from '../../hooks/useGetDisputData'
import { DisputStatus } from '../../model/types'
import { DisputStatusIcons, DisputStatusTitles } from '../../model/constants'
import { DownloadPicture } from '../downloadPicture/DownloadPicture'

type Input = {
  amount: string
}

type Props = {
  disputId: string
  isModalOpen: boolean
  setIsModalOpen: (value: boolean) => void
  onSubmit: (amount: number, topupId: string) => void
  onCancel: () => void
}

const INVALID_PAYMENT_ID = 'Нет данных'

export const OpenOrderDialog = ({
  disputId,
  isModalOpen,
  setIsModalOpen,
  onSubmit,
  onCancel,
}: Props) => {
  const { addToast } = useToast()

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Input>({
    defaultValues: {
      amount: '',
    },
  })

  const {
    disputData,
    error: disputError,
    isPending: isDisputPending,
  } = useGetDisputData({ disputId })

  const {
    data: queryTopupData,
    error: topupError,
    isPending: isTopupPending,
  } = useApiQuery<TopupModel>(
    ['topup', disputData?.paymentId],
    topupsAPI.item(disputData.paymentId),
    undefined,
    { enabled: Boolean(disputData.paymentId) && disputData.paymentId !== INVALID_PAYMENT_ID }
  )

  const { deviceData, isPending: isDevicePending } = useGetDeviceData({
    deviceId: queryTopupData?.deviceProfile.id ?? '',
  })

  useEffect(() => {
    if (disputError) {
      addToast('Ошибка при загрузке данных!', disputError.message, 'error')
    }
  }, [disputError, addToast])

  useEffect(() => {
    if (topupError) {
      addToast('Ошибка при загрузке данных!', topupError.message, 'error')
    }
  }, [topupError, addToast])

  const onSubmitHandler = (data: Input) => {
    if (data.amount) {
      onSubmit(Number(data.amount), queryTopupData?.id || '')
    } else {
      onSubmit(disputData.disputAmountFromMerchant, queryTopupData?.id || '')
    }
    reset()
  }

  const onCloseHandler = () => {
    setIsModalOpen(false)
    reset()
  }
  const requisite =
    queryTopupData?.paymentRequisitProfile.type && queryTopupData?.paymentRequisitProfile.value
      ? formatRequisitToType(
          queryTopupData?.paymentRequisitProfile.type,
          queryTopupData?.paymentRequisitProfile.value
        )
      : ''

  const iconClass = 'text-primary dark:text-primary-dark'
  const blockConfig = [
    {
      title: 'Сделка',
      icon: <DealIcon status={TopupStatusIcons[queryTopupData?.status || TopupStatus.Unknown]} />,
      titleText: TopupStatusTitles[queryTopupData?.status || TopupStatus.Unknown],
      subtitleText: formatDate(queryTopupData?.createdAt || ''),
    },
    {
      title: 'Реквизит',
      icon: (
        <PaymentRequisitIcon
          accountType={queryTopupData?.paymentRequisitProfile.type}
          status={queryTopupData?.paymentRequisitProfile.status}
          bg
        />
      ),
      titleText: queryTopupData?.requisitProfile.recipientName || '',
      subtitleText: requisite,
    },
    {
      title: 'Устройство',
      icon: <DeviceIcon className={iconClass} status={DeviceStatusIcons[deviceData.status]} />,
      titleText: deviceData.name || '',
      subtitleText: queryTopupData?.deviceProfile.id || '',
    },
  ]

  const infoBlocks = blockConfig.map(({ title, icon, titleText, subtitleText }) => ({
    title,
    data: (
      <ItemLayout
        icon={icon}
        title={titleText}
        subtitle={subtitleText}
        titleClasses="font-medium"
        subtitleClasses="font-medium"
      />
    ),
  }))

  return (
    <BaseDialog
      isLoading={isDisputPending}
      isVisible={isModalOpen}
      onClose={onCloseHandler}
      onStepBack={disputData.status !== DisputStatus.Reopened ? onCancel : onCloseHandler}
      closeButton={disputData.status !== DisputStatus.Reopened}
      onAction={handleSubmit(onSubmitHandler)}
      actionButtonName="Принять спор"
      closeButtonName="Отклонить спор"
      iconTitle={
        isDisputPending ? (
          <ItemLayoutSkeleton />
        ) : (
          <ItemLayout
            icon={
              <OrderIcon
                status={DisputStatusIcons[disputData.status]}
                className="w-[52px] h-[52px]"
              />
            }
            title={DisputStatusTitles[disputData.status]}
            subtitle={getTimeAgoFormatted(disputData.createdAt)}
            titleClasses="text-[20px] !font-klein"
            subtitleClasses="!text-primary dark:!text-primary-dark font-klein font-medium"
          />
        )
      }
    >
      <div className="pt-6 border-t-1 border-t-card-border dark:border-t-card-border-dark flex flex-col gap-6">
        <>
          {isDisputPending ? (
            <BaseDialogSkeleton />
          ) : (
            <>
              {isTopupPending && isDevicePending ? (
                [...Array(3)].map((_, i) => (
                  <div key={i}>
                    <span className="text-text-secondary inline-block mb-2">&nbsp;</span>
                    <ItemLayoutSkeleton />
                  </div>
                ))
              ) : (
                <>
                  {infoBlocks.map((el, i) => (
                    <div key={i}>
                      <span className="text-text-secondary inline-block mb-2">{el.title}</span>
                      {el.data}
                    </div>
                  ))}

                  <div>
                    <span className="text-text-secondary inline-block mb-2">Прикрепленный чек</span>
                    <div className="bg-light-purple dark:bg-dark-purple px-2 py-4 rounded-lg">
                      <DownloadPicture disput={disputData} />
                    </div>
                  </div>
                </>
              )}
              <div>
                <Controller
                  control={control}
                  name="amount"
                  rules={{
                    pattern: {
                      value: /^\d+$/,
                      message: 'Введите только цифры',
                    },
                  }}
                  render={({ field }) => (
                    <BaseInput
                      {...field}
                      onBlur={() => Promise.resolve(field.onBlur())}
                      error={errors.amount?.message}
                      label="Сумма перевода"
                      placeholder="Укажите сумму перевода"
                      className="mb-4"
                    />
                  )}
                />
              </div>
            </>
          )}
        </>
      </div>
    </BaseDialog>
  )
}
